import React, { FC, useCallback, useMemo } from "react";
import { Box } from "@ldp-web/component-library/ui/atoms/box";
import { Filters } from "@ldp-web/component-library/ui/molecules/filter/filters";
import { CategoryButton } from "@ldp-web/component-library/ui/organisms/navigation/category";
import { Filter } from "@ldp-web/component-library/ui/organisms/navigation/filter";
import {
  TSDMFilterGroup,
  useFilterTracking,
  useSortTracking,
} from "@ldp/sdm-analytics-utils";
import { TSDMSelectedFiltersType } from "@ldp/common-types";
import {
  usePlpContext,
  useSDMRouterContext,
  useSDMViewportContext,
} from "@ldp/sdm-contexts";
import { handleFilterSelect, handleSortSelect, useRedirectLink } from "../../utils";
import {
  useRemoveAllQueryParams,
  useUpdateFilterQueryParams,
} from "../../hooks";
import { useLayoutSectionV2 } from "@ldp/view-resolver-lite";
import { useSimpleTranslation } from "@ldp/simple-i18n";
import { Option } from "@ldp-web/component-library/types";

/** Sort data type
 *
 */
interface ISortData {
  /** sort dropdown options */
  options: Option[];
  /** child category navigation nodes */
  labelPrefix: string;
  /** sort heading */
  heading: string;
}

/** Filter prop types */
export interface IFiltersComponentProps {
  /** filter title */
  filtersLabel: string;
  /** apply button text used to replace "show all results" button text in filters modal */
  applyButtonText?: string;
  /** Filter group data */
  filterGroups: TSDMFilterGroup[] | null;
  /** number of results */
  totalResults?: number | null;
  /** method to track analytics event */
  trackingFn?: () => void;
  /** flag for listing page */
  isListingPage?: boolean;
  /** filter groups that should be open by default */
  defaultAccordionIndex: number[];
}
/** Component to render Filters */
export const FiltersComponent: FC<IFiltersComponentProps> = ({
  filterGroups,
  filtersLabel,
  applyButtonText,
  trackingFn,
  totalResults,
  defaultAccordionIndex,
}) => {
  const {
    drawerRef,
    shouldShowDrawer,
    setShouldShowDrawer,
    drawerDefaultAccordionIndex,
    setDrawerDefaultAccordionIndex,
  } = usePlpContext();
  const i18n = useSimpleTranslation();
  const sortsSection = useLayoutSectionV2("sortsSection");

  const sortResponse = sortsSection?.[0].data?.sorts;

  /** sort component props */
  const sortData: ISortData = {
    options: (sortResponse as Option[]) || [],
    heading: i18n("sort"),
    labelPrefix: i18n("sortBy"),
  };
  const { viewport } = useSDMViewportContext();
  const isDesktopView = viewport === "desktop";
  const noOfResults = totalResults?.toString() || "";
  const router = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const updateFilterQueryParams = useUpdateFilterQueryParams();
  const removeAllQueryParams = useRemoveAllQueryParams();
  const trackFilterFn = useFilterTracking();
  const trackSortFn = useSortTracking();
  const filtersA11yLabels = {
    crossIconLabel: i18n("a11yClearSearchTerm"),
    searchPlaceholderText: i18n("search"),
    filterLegend: i18n("a11yFilterLegend"),
    selectedFilterIndicator: i18n("a11ySelectedFilters"),
  };

  const updatedFilterData = useMemo(() => {
    return filterGroups?.map((group) => ({
      ...group,
      values: group.values.filter(({ code }) => code),
    }));
  }, [filterGroups]);

  const handleAccordionClick = useCallback(
    (val: number | number[]) => {
      setDrawerDefaultAccordionIndex(val);
    },
    [setDrawerDefaultAccordionIndex]
  );

  const handleFilterClick = (val: TSDMSelectedFiltersType) => {
    handleFilterSelect(
      val,
      filterGroups,
      updateFilterQueryParams,
      trackFilterFn,
      redirectToPage
    );
  };

  const mobileFilterProps = {
    title: filtersLabel,
    handleOnChange: handleFilterClick,
    trackEvent: trackingFn,
    filterGroups: updatedFilterData,
    onAccordionChange: handleAccordionClick,
    defaultAccordionIndex: drawerDefaultAccordionIndex,
    a11yLabels: filtersA11yLabels,
  };

  const buttonClick = useCallback(() => {
    setShouldShowDrawer(!shouldShowDrawer);
  }, [shouldShowDrawer, setShouldShowDrawer]);

  const onClose = useCallback(() => {
    setShouldShowDrawer(false);
  }, [setShouldShowDrawer]);

  const ctaData = {
    showAllResults: applyButtonText ?? i18n("showAllResults"),
    showResults: applyButtonText ?? i18n("showResults"),
    handleOnClick: onClose,
  };

  /**
   * Function to handle sort click
   * @param val selected value of sort
   * updates query parameter and selected sort value, makes api call
   */
  const handleSortClick = (val: string) => {
    const queryParam = handleSortSelect(val, sortData.options);
    /** to track analytics */
    trackSortFn({
      sortEventValue: val,
    });
    const updatedUrl = updateFilterQueryParams(queryParam, true);
    redirectToPage(updatedUrl);
  };

  const filterSortData = {
    onSelected: handleSortClick,
    ...sortData,
  };

  const removeAllFilters = () => {
    removeAllQueryParams();
  };

  if (!filterGroups) return null;

  return (
    <>
      {!isDesktopView &&
        mobileFilterProps?.filterGroups &&
        mobileFilterProps?.filterGroups?.length > 0 && (
          <Box
            display={{ base: "inline-flex", lg: "none" }}
            style={{ justifyContent: "flex-start", width: "100%" }}
          >
            <Box style={{ width: "100%" }}>
              <CategoryButton onClick={buttonClick} width="100%">
                {filtersLabel}
              </CategoryButton>
            </Box>
            <Filter
              title={filtersLabel}
              onClose={onClose}
              select={sortData && filterSortData}
              filters={mobileFilterProps as any}
              submitCTA={ctaData}
              handleClearAll={removeAllFilters}
              clearAllLabel={i18n("clearAll")}
              ref={drawerRef as any}
              numberOfFilterResults={noOfResults}
              isOpen={shouldShowDrawer}
            />
          </Box>
        )}
      {isDesktopView && filterGroups && filterGroups?.length > 0 && (
        <Box display={{ base: "none", lg: "block" }} id="site-filter-content">
          <Filters
            filterGroups={updatedFilterData as any}
            title={filtersLabel}
            handleOnChange={handleFilterClick}
            trackEvent={trackingFn}
            a11yLabels={filtersA11yLabels}
            defaultAccordionIndex={defaultAccordionIndex}
          />
        </Box>
      )}
    </>
  );
};

export default FiltersComponent;
